import { useState, useEffect, useCallback } from 'react';
import type { Station } from '@shared/types';

const STORAGE_KEY = 'kitchen-selected-station';

// Minimal fallback for fast startup; the API replaces this once it returns.
// Real station locations live in the `stations` table (see migrations/0001).
const DEFAULT_STATIONS: Station[] = [{ id: 1, name: 'Station', code: null, is_active: true }];

export function useStations() {
  const [stations, setStations] = useState<Station[]>(DEFAULT_STATIONS);
  const [selectedStation, setSelectedStation] = useState<Station | null>(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) return JSON.parse(saved) as Station;
    } catch {
      // ignore
    }
    return null;
  });

  useEffect(() => {
    let cancelled = false;
    fetch('/api/stations', { credentials: 'include' })
      .then((r) => (r.ok ? (r.json() as Promise<{ stations: Station[] }>) : null))
      .then((data) => {
        if (!cancelled && data?.stations?.length) {
          setStations(data.stations);
        }
      })
      .catch(() => {
        // Keep defaults on failure
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const selectStation = useCallback((station: Station) => {
    setSelectedStation(station);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(station));
  }, []);

  const clearStation = useCallback(() => {
    setSelectedStation(null);
    localStorage.removeItem(STORAGE_KEY);
  }, []);

  return { stations, selectedStation, selectStation, clearStation };
}
